import React from 'react';
import { useTranslation } from 'react-i18next';

const CATEGORY_LABELS = {
    core_performance: 'Core Performance',
    ai_driven: 'AI-Driven',
    comparison_trend: 'Comparison & Trends',
    footfall_conversion: 'Footfall & Conversion',
    category_vertical: 'Category / Vertical',
    command_center: 'Command Center',
};

/**
 * Widget Catalog — Sprint 2 F1.
 *
 * Slide-in panel that shows available widgets grouped by category.
 * Click a card to add it to the dashboard.
 *
 * Widgets already on the dashboard are highlighted and not clickable —
 * each widget can only be added once per layout. `addedIds` is a Set of
 * widget_id strings currently on the grid.
 */
export default function WidgetCatalog({ catalog, addedIds, onAdd, onClose }) {
    const { t } = useTranslation();
    const added = addedIds instanceof Set ? addedIds : new Set(addedIds || []);
    const grouped = catalog.reduce((acc, w) => {
        (acc[w.category] = acc[w.category] || []).push(w);
        return acc;
    }, {});

    return (
        <div
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
                zIndex: 9999, display: 'flex', justifyContent: 'flex-end',
            }}
            onClick={onClose}
        >
            <div
                onClick={e => e.stopPropagation()}
                className="v3-card"
                style={{
                    width: 480, height: '100vh', borderRadius: 0,
                    overflowY: 'auto', padding: 20,
                    background: 'var(--v3-bg-elevated)',
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
                    <h2 className="v3-heading" style={{ margin: 0 }}>{t('Widget Catalog')}</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('Close widget catalog')}
                        style={{
                            marginLeft: 'auto', cursor: 'pointer', fontSize: 22,
                            color: 'var(--v3-text-muted)', background: 'transparent',
                            border: 'none', padding: '0 6px', lineHeight: 1,
                        }}
                    >×</button>
                </div>

                {Object.entries(grouped).map(([cat, widgets]) => (
                    <div key={cat} style={{ marginBottom: 20 }}>
                        <div className="v3-label" style={{ marginBottom: 8 }}>{t(CATEGORY_LABELS[cat] || cat)}</div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                            {widgets.map(w => {
                                const isAdded = added.has(w.widget_id);
                                const handleAdd = () => { if (!isAdded) onAdd(w); };
                                return (
                                    <div
                                        key={w.widget_id}
                                        onClick={handleAdd}
                                        role="button"
                                        tabIndex={isAdded ? -1 : 0}
                                        aria-disabled={isAdded || undefined}
                                        onKeyDown={(e) => {
                                            if (isAdded) return;
                                            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAdd(w); }
                                        }}
                                        aria-label={isAdded ? `${w.label} widget (already added)` : `Add ${w.label} widget`}
                                        className={`v3-card v3-widget-cat-card${isAdded ? ' is-added' : ''}`}
                                        style={{
                                            padding: 12,
                                            cursor: isAdded ? 'default' : 'pointer',
                                            position: 'relative',
                                            borderLeft: isAdded
                                                ? '2px solid var(--v3-text-success, #10B981)'
                                                : (w.requires_ai ? '2px solid var(--v3-teal)' : undefined),
                                            opacity: isAdded ? 0.72 : 1,
                                            background: isAdded
                                                ? 'rgba(16,185,129,0.08)'
                                                : undefined,
                                        }}
                                    >
                                        {isAdded && (
                                            <span
                                                className="v3-widget-cat-badge"
                                                aria-hidden="true"
                                                style={{
                                                    position: 'absolute',
                                                    top: 6,
                                                    right: 8,
                                                    fontSize: 10,
                                                    fontWeight: 700,
                                                    letterSpacing: '0.04em',
                                                    color: 'var(--v3-text-success, #10B981)',
                                                    textTransform: 'uppercase',
                                                }}
                                            >
                                                ✓ {t('Added')}
                                            </span>
                                        )}
                                        <div className="v3-title" style={{ fontSize: 12, marginBottom: 4, paddingRight: isAdded ? 56 : 0 }}>
                                            {w.requires_ai && <span className="v3-ai-tag" style={{ marginRight: 6 }}>AI</span>}
                                            {w.label}
                                        </div>
                                        <div className="v3-body" style={{ fontSize: 10 }}>
                                            {w.chart_type} · {w.default_size?.w}×{w.default_size?.h}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
